/**
 * Convert TipTap JSON to HTML
 * Handles all TipTap node types and marks
 */

export const renderTipTapToHTML = (content) => {
  if (!content || typeof content !== 'object') {
    return '<p>Content unavailable</p>';
  }

  if (content.type !== 'doc' || !Array.isArray(content.content)) {
    return '<p>Invalid content format</p>';
  }

  return content.content.map(node => renderNode(node)).join('');
};

const renderNode = (node) => {
  if (!node || !node.type) return '';

  switch (node.type) {
    case 'paragraph':
      return renderParagraph(node);
    case 'heading':
      return renderHeading(node);
    case 'bulletList':
      return renderBulletList(node);
    case 'orderedList':
      return renderOrderedList(node);
    case 'listItem':
      return renderListItem(node);
    case 'blockquote':
      return renderBlockquote(node);
    case 'horizontalRule':
      return '<hr class="my-8 border-t-2 border-gray-200" />';
    case 'image':
    case 'resizableImage':
    case 'imageResize':
      return renderImage(node);
    case 'hardBreak':
      return '<br />';
    default:
      return '';
  }
};

const renderParagraph = (node) => {
  const textAlign = node.attrs?.textAlign || 'left';
  const content = renderInlineContent(node.content || []);
  
  if (!content.trim()) {
    return '<p class="mb-4">&nbsp;</p>';
  }
  
  let alignStyle = '';
  if (textAlign === 'justify') {
    alignStyle = 'text-align: justify;';
  } else {
    alignStyle = `text-align: ${textAlign};`;
  }
  
  return `<p class="mb-4" style="${alignStyle}">${content}</p>`;
};

const renderHeading = (node) => {
  const level = node.attrs?.level || 2;
  const textAlign = node.attrs?.textAlign || 'left';
  const content = renderInlineContent(node.content || []);
  
  const classes = {
    1: 'text-4xl font-bold mt-8 mb-4',
    2: 'text-3xl font-bold mt-8 mb-4',
    3: 'text-2xl font-semibold mt-6 mb-3',
    4: 'text-xl font-semibold mt-5 mb-2',
    5: 'text-lg font-semibold mt-4 mb-2',
    6: 'text-base font-semibold mt-3 mb-2'
  };
  
  return `<h${level} class="${classes[level] || classes[2]}" style="text-align: ${textAlign}">${content}</h${level}>`;
};

const renderBulletList = (node) => {
  const items = (node.content || []).map(item => renderNode(item)).join('');
  return `<ul class="list-disc pl-6 mb-4 space-y-2">${items}</ul>`;
};

const renderOrderedList = (node) => {
  const items = (node.content || []).map(item => renderNode(item)).join('');
  return `<ol class="list-decimal pl-6 mb-4 space-y-2">${items}</ol>`;
};

const renderListItem = (node) => {
  const content = (node.content || []).map(child => {
    if (child.type === 'paragraph') {
      return renderInlineContent(child.content || []);
    }
    return renderNode(child);
  }).join('');
  
  return `<li class="text-gray-700">${content}</li>`;
};

const renderBlockquote = (node) => {
  const content = (node.content || []).map(child => renderNode(child)).join('');
  return `<blockquote class="border-l-4 border-blue-500 pl-4 py-2 my-4 italic text-gray-600 bg-gray-50 rounded-r">${content}</blockquote>`;
};

const renderImage = (node) => {
  const src = node.attrs?.src || '';
  const alt = node.attrs?.alt || '';
  const title = node.attrs?.title || '';
  const textAlign = node.attrs?.textAlign || 'left';
  const width = node.attrs?.width || '';
  const height = node.attrs?.height || '';
  
  if (!src) {
    return '';
  }
  
  let alignClass = 'block';
  if (textAlign === 'center') {
    alignClass = 'block mx-auto';
  } else if (textAlign === 'right') {
    alignClass = 'block ml-auto';
  }
  
  let styleStr = 'max-width: 100%; height: auto;';
  if (width && width !== 'auto') {
    styleStr += ` width: ${typeof width === 'number' ? width + 'px' : width};`;
  }
  if (height && height !== 'auto') {
    styleStr += ` height: ${typeof height === 'number' ? height + 'px' : height};`;
  }
  
  // Use src directly without escaping to avoid breaking URLs
  const escapedAlt = escapeHtml(alt);
  const escapedTitle = title ? escapeHtml(title) : '';
  
  const imgHtml = `<img src="${src}" alt="${escapedAlt}" ${escapedTitle ? `title="${escapedTitle}"` : ''} class="rounded-lg my-6 ${alignClass}" style="${styleStr}" loading="lazy" />`;
  return imgHtml;
};

const renderInlineContent = (content) => {
  if (!Array.isArray(content)) return '';
  
  return content.map(node => {
    if (node.type === 'text') {
      return applyMarks(node.text || '', node.marks || []);
    }
    if (node.type === 'hardBreak') {
      return '<br />';
    }
    return '';
  }).join('');
};

const applyMarks = (text, marks) => {
  if (!Array.isArray(marks) || marks.length === 0) {
    return escapeHtml(text);
  }
  
  let result = escapeHtml(text);
  
  // Apply marks in order
  marks.forEach(mark => {
    switch (mark.type) {
      case 'bold':
        result = `<strong>${result}</strong>`;
        break;
      case 'italic':
        result = `<em>${result}</em>`;
        break;
      case 'underline':
        result = `<u>${result}</u>`;
        break;
      case 'strike':
        result = `<s>${result}</s>`;
        break;
      case 'code':
        result = `<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-red-600">${result}</code>`;
        break;
      case 'link':
        const href = mark.attrs?.href || '#';
        const target = mark.attrs?.target || '_blank';
        result = `<a href="${href}" target="${target}" rel="noopener noreferrer" class="text-orange-500 underline hover:text-orange-600">${result}</a>`;
        break;
      case 'textStyle':
        const fontSize = mark.attrs?.fontSize;
        const fontFamily = mark.attrs?.fontFamily;
        let style = '';
        if (fontSize) style += `font-size: ${fontSize};`;
        if (fontFamily) style += `font-family: ${fontFamily};`;
        if (style) {
          result = `<span style="${style}">${result}</span>`;
        }
        break;
      case 'color':
        const color = mark.attrs?.color;
        if (color) {
          result = `<span style="color: ${color}">${result}</span>`;
        }
        break;
      case 'highlight':
        const highlightColor = mark.attrs?.color || '#FEF3C7';
        result = `<mark style="background-color: ${highlightColor}; padding: 2px 4px; border-radius: 2px;">${result}</mark>`;
        break;
      default:
        break;
    }
  });
  
  return result;
};

const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};
